import type { FeedbackItem } from '../types';

/**
 * Where a pin sits, in *page* coordinates (document space, unaffected by
 * scroll).
 */
export interface PinPoint {
  left: number;
  top: number;
}

/**
 * Page dimensions may differ from save-time by this fraction and still be
 * considered "the same layout", in which case the exact saved coordinates are
 * used. Beyond it the pin is scaled proportionally instead.
 */
const LAYOUT_MATCH_TOLERANCE = 0.02;

/**
 * Resolve a pin to page coordinates.
 *
 * Priority:
 *  1. Element-anchored: query the stored selector and add the saved offset, so
 *     the pin sticks to its element through reflow. `getBoundingClientRect` is
 *     viewport-relative, so scroll is added back to get page coordinates.
 *  2. Saved page coordinates, when the document is currently about the same
 *     size as it was at save time. This is the common case — same page, same
 *     content — and reproduces the original placement exactly.
 *  3. Proportional fallback for a genuinely different layout (responsive
 *     breakpoint, viewport preset), scaling the saved percentages.
 *
 * Shared by the pin overlay and by "scroll to this pin" in the panel, so the
 * two can't disagree about where a pin is.
 */
/**
 * True when there is a live DOM to measure a pin against.
 *
 * A pin's position is a *measurement* — of an element's box, of the document's
 * size — and during server rendering there is nothing to measure. Callers use
 * this to skip the work rather than lean on the fallback below.
 */
export function canMeasureLayout(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}

/**
 * The element a pin is anchored to, or null when the pin has no anchor or it
 * isn't on the page.
 */
export function findPinElement(
  pinLocation: NonNullable<FeedbackItem['pinLocation']>,
  doc: Document
): Element | null {
  if (
    !pinLocation.elementSelector ||
    pinLocation.elementOffsetX == null ||
    pinLocation.elementOffsetY == null
  ) {
    return null;
  }
  try {
    return doc.querySelector(pinLocation.elementSelector);
  } catch (_) {
    // Invalid selector — the caller falls back to coordinates.
    return null;
  }
}

/**
 * Page coordinates of a pin anchored to `el`, from where the element is
 * *drawn* right now. That includes transforms, so on a page that moves
 * elements as it scrolls — parallax, smooth-scroll libraries, pinned
 * sections — the answer changes with scroll and has to be re-read.
 */
export function anchoredPinPoint(
  pinLocation: NonNullable<FeedbackItem['pinLocation']>,
  el: Element,
  view: Window
): PinPoint {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + view.scrollX + (pinLocation.elementOffsetX ?? 0),
    top: rect.top + view.scrollY + (pinLocation.elementOffsetY ?? 0),
  };
}

export function computePinPoint(
  pinLocation: NonNullable<FeedbackItem['pinLocation']>,
  // The window the pin lives in — the device preview's, while one is open.
  win?: Window
): PinPoint {
  // No DOM: this runs during server rendering, including the single server
  // pass Next.js gives a "use client" component while prerendering a static
  // page. Reaching `document.querySelector` below threw
  // `ReferenceError: window is not defined` and failed the whole build of any
  // app rendering the widget in a layout.
  //
  // The saved page coordinates are the only honest answer without a viewport,
  // and they are what the "same layout" branch returns anyway — which is the
  // common case of reopening the same page at the same size.
  if (!canMeasureLayout()) {
    return { left: pinLocation.pageX ?? 0, top: pinLocation.pageY ?? 0 };
  }

  const view = win ?? window;
  const doc = view.document;

  const el = findPinElement(pinLocation, doc);
  if (el) return anchoredPinPoint(pinLocation, el, view);

  const currentWidth = doc.documentElement.scrollWidth || view.innerWidth;
  const currentHeight = doc.documentElement.scrollHeight || view.innerHeight;

  // Scaling by live scrollHeight on every recompute is what makes pins crawl
  // down the page while images and fonts are still loading: the denominator
  // keeps growing. When the page is effectively the size it was at save time,
  // trust the saved coordinates instead.
  const sameishWidth =
    !!pinLocation.pageWidth &&
    Math.abs(currentWidth - pinLocation.pageWidth) / pinLocation.pageWidth <
      LAYOUT_MATCH_TOLERANCE;
  const sameishHeight =
    !!pinLocation.pageHeight &&
    Math.abs(currentHeight - pinLocation.pageHeight) / pinLocation.pageHeight <
      LAYOUT_MATCH_TOLERANCE;

  const left =
    sameishWidth && pinLocation.pageX != null
      ? pinLocation.pageX
      : typeof pinLocation.percentX === 'number'
        ? (pinLocation.percentX / 100) * currentWidth
        : pinLocation.pageX || 0;

  const top =
    sameishHeight && pinLocation.pageY != null
      ? pinLocation.pageY
      : typeof pinLocation.percentY === 'number'
        ? (pinLocation.percentY / 100) * currentHeight
        : pinLocation.pageY || 0;

  return { left, top };
}
